import { useEffect, useRef } from "react";

/**
 * Additive self-heal for realtime-driven UI.
 *
 * Subscribes to window `focus` and document `visibilitychange` and calls
 * `refetch()` (debounced 500ms) when the tab becomes active. Pair with a
 * `SUBSCRIBED`-triggered refetch inside your `.subscribe()` callback and
 * any realtime `CHANNEL_ERROR` gap self-heals — matches the DM pattern
 * in `ChatContext.tsx` and `useUnreadJoinRequests`.
 *
 * Pass `enabled=false` while dependencies aren't ready to avoid noise.
 */
export function useRealtimeSelfHeal(refetch: () => void, enabled: boolean = true): void {
  // Keep the latest refetch in a ref so callers don't have to memoize it.
  const refetchRef = useRef(refetch);
  useEffect(() => { refetchRef.current = refetch; }, [refetch]);

  useEffect(() => {
    if (!enabled) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const runDebounced = () => {
      if (timer) return;
      timer = setTimeout(() => { timer = null; try { refetchRef.current(); } catch { /* noop */ } }, 500);
    };
    const onFocus = () => runDebounced();
    const onVis = () => { if (document.visibilityState === "visible") runDebounced(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
      if (timer) clearTimeout(timer);
    };
  }, [enabled]);
}
